import React from 'react';
import { get, isEmpty } from 'lodash';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import { getModelState } from 'client/data/luckdragon/model';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { EdmundsNewCarsNoAdhesion } from 'site-modules/shared/pages/decorators/edmunds-new-cars-no-adhesion';
import {
  buildPubStateModelYearPath,
  buildVehiclePath,
  VehicleModel,
  buildFuelCostsPath,
  buildEvTestedDataPath,
  buildMakeModelDefaultYear,
} from 'client/data/models/vehicle-v2';
import { buildMpgStylesPath, MpgModel } from 'client/data/models/mpg';
import { buildSimilarVehiclesCrossShopPath, CoreResearchModel } from 'client/data/models/core-research';
import { buildReviewPath, EditorialReviewModel } from 'client/data/models/editorial-review';
import { getTreehouseEligiblePath, TreehouseModel } from 'client/data/models/treehouse';
import { URL_CONTEXT_PATH, UrlModel } from 'client/data/models/url';
import { buildAllPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { buildCoreBreadcrumbs, getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getCoreUrlPattern } from 'site-modules/shared/utils/core-page/url-pattern';
import { getMpgPageCarSchema } from 'client/seo/schemas/car';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { ELECTRIC_PAGE_DISPLAY_NAME, PAGE_DISPLAY_NAME, URL_PATH } from 'site-modules/shared/constants/mpg';
import { NativeAdsCreativeConfigModel } from 'client/data/models/native-ads-creative-config';
import { BUILD_PRICE_AD, INSIGHT_AD } from 'client/site-modules/shared/components/native-ad/utils/constants';
import {
  DEFAULT_PERSONALIZATION_PARAMS,
  EV_TESTED_DATA_START_DATE,
} from 'site-modules/shared/constants/range-and-cost';
import { setCriticalCss } from 'client/actions/critical-css';
import { PageModel } from 'client/data/models/page';
import {
  getCreativeConfigAdPathByVehicle,
  setAdsListToPreload,
  siteServedAdsCriticalCss,
} from 'client/site-modules/shared/components/native-ad/utils/utils';
import { getQuery } from 'client/utils/location';

import { Mpg } from './mpg';

const PAGE_NAME = 'model_core_mpg';
const PAGE_CATEGORY = 'model_core';

export const pageOptions = {
  name: PAGE_NAME,
  category: PAGE_CATEGORY,
  component: props => <Mpg {...props} params={props.match.params} />,
  preload: (store, props) => {
    const params = props.match.params;
    const styleId = getQuery(props.location).style;

    const preloader = new VehiclePreloader(store, { ...props, params });

    preloader.set(URL_CONTEXT_PATH, UrlModel, {
      urlPattern: getCoreUrlPattern(params.year, params.submodel, 'mpg'),
    });

    const { mobile } = store.getState();

    return preloader.load().then(modelState => {
      const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
      const vehicleParams = getParamsFromVehicle(makeModelSubmodelYear);

      const isUsed = isUsedPubState(makeModelSubmodelYear.pubStates);

      store.dispatch(
        legacyPageUpdate({
          pageName: `${isUsed ? 'used' : 'new'}_${PAGE_NAME}`,
          categoryName: `${isUsed ? 'used' : 'new'}_${PAGE_CATEGORY}`,
        })
      );

      preloader.resolve(buildMakeModelDefaultYear(params), VehicleModel);
      preloader.resolve(buildPubStateModelYearPath(), VehicleModel);
      preloader.resolve(buildMpgStylesPath(params), MpgModel);
      preloader.resolve(
        buildSimilarVehiclesCrossShopPath(makeModelSubmodelYear.model.modelLinkCode),
        CoreResearchModel
      );

      // hero photo
      preloader.resolve(buildReviewPath(params), EditorialReviewModel);

      preloader.resolve(
        buildAllPhotosPath({
          ...vehicleParams,
          pageNum: 1,
          submodel: makeModelSubmodelYear.submodels.name,
          imageSize: 600,
        }),
        PhotoflipperModel
      );

      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

      const vehiclePageContext = modelState.get('vehicle', PageModel);
      setAdsListToPreload(
        preloader,
        store.getState(),
        [BUILD_PRICE_AD.AD_NAME, INSIGHT_AD.AD_NAME],
        vehiclePageContext
      );

      return preloader.load().then(modelStateObj => {
        const styles = modelStateObj.get(buildMpgStylesPath(params), MpgModel);
        const currentStyle = !!styles && (styles.find(({ id }) => id === styleId) || styles[0]);

        const isPHEV =
          get(currentStyle, 'engineType') === 'plug-in hybrid' ||
          (get(currentStyle, 'engineType') === 'hybrid' && !!get(currentStyle, 'evData.range'));

        preloader.resolve(
          buildFuelCostsPath(get(currentStyle, 'id'), {
            milesPerYear: DEFAULT_PERSONALIZATION_PARAMS.milesPerYear,
            cityPercents: DEFAULT_PERSONALIZATION_PARAMS.cityPercents,
            dailyCommute: isPHEV ? DEFAULT_PERSONALIZATION_PARAMS.dailyCommute : undefined,
          }),
          VehicleModel
        );

        const isElectric = get(currentStyle, 'engineType') === 'electric';
        if (isElectric && params.year >= EV_TESTED_DATA_START_DATE) {
          preloader.resolve(buildEvTestedDataPath([get(currentStyle, 'id')]), VehicleModel);
        }

        if (isPHEV || isElectric) {
          preloader.resolve(getTreehouseEligiblePath(), TreehouseModel);
        }

        return preloader.load().then(state => {
          const evTestedData = get(state.get(buildEvTestedDataPath([get(currentStyle, 'id')]), VehicleModel), '[0]');

          const siteServedAds = state.get(
            getCreativeConfigAdPathByVehicle(mobile, vehiclePageContext),
            NativeAdsCreativeConfigModel
          );

          store.dispatch(
            setCriticalCss({
              engineType: get(currentStyle, 'engineType'),
              hasEvTestedData: !isEmpty(evTestedData),
              ...siteServedAdsCriticalCss(siteServedAds),
            })
          );

          return preloader.load();
        });
      });
    });
  },
  seo: (state, props) => {
    const params = props.match.params;
    const modelState = getModelState(state);
    const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
    const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleModel);
    const mpgStyles = modelState.get(buildMpgStylesPath(params), MpgModel);

    const displayVehicle = buildDisplayVehicle({ makeModelSubmodelYear, params });
    const shortDisplayVehicle = buildDisplayVehicle({ makeModelSubmodelYear, params, usedPrefix: '' });

    const isElectric = Object.keys(get(makeModelSubmodelYear, 'edmundsTypeCategories', {})).includes('electric');
    const pageDisplayName = isElectric ? ELECTRIC_PAGE_DISPLAY_NAME : PAGE_DISPLAY_NAME;

    const title = `${displayVehicle} ${pageDisplayName} | Edmunds`;
    const description = `View detailed gas mileage data for the ${shortDisplayVehicle}. Use our handy tool to get estimated annual fuel costs based on your driving habits.`;

    const seoObject = {
      title,
      description,
      jsonld: [
        getBreadcrumbSchema(
          buildCoreBreadcrumbs({
            makeModelSubmodelYear,
            params,
            defaultYear,
            pageDisplayName,
            urlPath: getUrlPathFromParams(params, URL_PATH),
            includeLastLink: true,
          })
        ),
      ],
    };

    seoObject.jsonld.push(
      getMpgPageCarSchema({
        makeModelSubmodelYear,
        seoObject,
        mpgStyle: get(mpgStyles, '[0]'),
      })
    );

    return seoObject;
  },
  decorator: EdmundsNewCarsNoAdhesion,
  options: {
    hasVehicleContext: true,
  },
};

export default pageDefinition(pageOptions);
